import { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { CALLABLE_BEER_ADD_BEER, functions } from '../utils/firebase';
import * as Sentry from '@sentry/react';
import { processImportResponse } from '../utils/addBeerResponse';
import {
  extractBeersFromScrapingResult,
  transformBeerToImport,
} from '../utils/beerStyle/scrapedBeerImport';

/**
 * filterBeersByIndices
 * Filters beers array to only include beers at the specified indices.
 */
function filterBeersByIndices(beers, selectedIndices) {
  if (!selectedIndices || selectedIndices.length === 0) {
    return beers;
  }
  const selectedBeers = [];
  beers.forEach((beerRow, beerIndex) => {
    if (selectedIndices.includes(beerIndex)) {
      selectedBeers.push(beerRow);
    }
  });
  return selectedBeers;
}

/**
 * setErrorIfDialogOpen
 * Sets error state only if dialog is still open.
 */
function setErrorIfDialogOpen(dialogOpenRef, setError, message) {
  if (dialogOpenRef.current) {
    setError(message);
  }
}

/**
 * useBeerImport
 * Provides beer import functionality from scraped data.
 *
 * @param {string} parentBreweryId - The brewery ID to import beers to
 * @param {Object} scrapingResult - The scraped beer data result
 * @param {string} selectedUrl - The URL that was scraped
 * @param {string} beerUrl - Alternative URL
 * @param {Object} styleMapping - Catalog name/nameKey/id → beerStyles id
 * @param {Object} styleTypeByName - Session type assignments for unknown names
 * @param {Object} dialogOpenRef - Ref to track if dialog is open
 * @param {Array<number>} selectedIndices - Array of beer indices to import
 * @returns {{ importBeers: Function, loading: boolean, error: string }}
 */
export function useBeerImport(
  parentBreweryId,
  scrapingResult,
  selectedUrl,
  beerUrl,
  styleMapping,
  styleTypeByName,
  dialogOpenRef,
  selectedIndices = null,
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const importBeers = useCallback(async () => {
    if (!scrapingResult || !parentBreweryId) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const scrapedBeers = extractBeersFromScrapingResult(scrapingResult);

      if (!scrapedBeers.length) {
        setErrorIfDialogOpen(dialogOpenRef, setError, 'No beers found to import.');
        return null;
      }

      const beersToProcess = filterBeersByIndices(
        scrapedBeers,
        selectedIndices,
      );

      if (!beersToProcess.length) {
        setErrorIfDialogOpen(
          dialogOpenRef,
          setError,
          'No beers selected to import.',
        );
        return null;
      }

      const beersToImport = beersToProcess
        .map((beer) =>
          transformBeerToImport(
            beer,
            styleMapping || {},
            styleTypeByName || {},
            parentBreweryId,
          ),
        )
        .filter((beer) => beer !== null && beer.name.trim() !== '');

      if (!beersToImport.length) {
        setErrorIfDialogOpen(
          dialogOpenRef,
          setError,
          'No valid beers to import. Assign a style type for unknown style names.',
        );
        return null;
      }

      const addBeerFn = httpsCallable(
        functions,
        CALLABLE_BEER_ADD_BEER,
        { timeout: 600000 },
      );
      const result = await addBeerFn({
        beers: beersToImport,
        breweryId: parentBreweryId,
        sourceType: 'url',
        source: selectedUrl || beerUrl || null,
      });

      const response = result?.data || {};

      if (!dialogOpenRef.current) {
        return null;
      }

      return processImportResponse(response);
    } catch (err) {
      if (!dialogOpenRef.current) {
        return null;
      }
      console.error('Error importing beers:', err);
      Sentry.captureException(err);
      setError(err?.message || 'Failed to import beers. Please try again.');
      return null;
    } finally {
      if (dialogOpenRef.current) {
        setLoading(false);
      }
    }
  }, [
    scrapingResult,
    parentBreweryId,
    styleMapping,
    styleTypeByName,
    selectedUrl,
    beerUrl,
    dialogOpenRef,
    selectedIndices,
  ]);

  return {
    importBeers,
    loading,
    error,
  };
}
